/* Grubwick admin — restaurant list + search + venue filter. */
import React, { useMemo, useState } from "react";
import type { VenueType } from "../../../data/adminData";
import { VENUES, fullAddress, type Restaurant } from "../../../data/adminData";
import { VenueTag } from "../ui/ui";
import { Plus, Search, X, Pin, List, Arrow, VENUE_META } from "../icons/icons";

export function ListView({
  restaurants,
  onOpen,
  onAdd,
  canAdd = true,
  canEdit = () => true,
}: {
  restaurants: Restaurant[];
  onOpen: (id: string) => void;
  onAdd: () => void;
  /* Whether to offer "Add restaurant" — admins only. */
  canAdd?: boolean;
  /* Whether the caller may edit a given restaurant; drives the card CTA
     ("Edit" vs "View"). Non-editable spots still open, read-only. */
  canEdit?: (id: string) => boolean;
}): React.ReactElement {
  const [q, setQ] = useState("");
  const [venue, setVenue] = useState<"All" | VenueType>("All");
  const venues: ("All" | VenueType)[] = ["All", ...VENUES];

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return restaurants.filter((r) => {
      if (venue !== "All" && r.venue !== venue) return false;
      if (!t) return true;
      return (
        r.name +
        " " +
        r.hood +
        " " +
        r.style +
        " " +
        fullAddress(r) +
        " " +
        r.menu.map((m) => m.name).join(" ")
      )
        .toLowerCase()
        .includes(t);
    });
  }, [restaurants, q, venue]);

  return (
    <div className="page">
      <div className="phead">
        <div>
          <p className="h-eyebrow">Catalog</p>
          <h1>Restaurants</h1>
          <p className="sub">
            Select a spot to edit its details and menu, or add a new one to the
            directory.
          </p>
        </div>
        {canAdd && (
          <button className="btn btn-primary" onClick={onAdd}>
            <Plus size={17} /> Add restaurant
          </button>
        )}
      </div>

      <div className="searchbar">
        <Search size={19} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search restaurants, neighborhoods, cuisines, or menu items…"
        />
        {q && (
          <button
            className="iconbtn"
            onClick={() => setQ("")}
            aria-label="Clear"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <div className="filter-row">
        {venues.map((v) => {
          const Ic = v !== "All" ? VENUE_META[v].Icon : null;
          return (
            <button
              key={v}
              className={"chip" + (venue === v ? " on" : "")}
              onClick={() => setVenue(v)}
            >
              {Ic && <Ic size={13} />}
              {v}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 22, marginBottom: 2 }}>
        <span className="count-line">
          {filtered.length} {filtered.length === 1 ? "spot" : "spots"}
          {venue !== "All" ? " · " + venue : ""}
        </span>
      </div>

      {filtered.length ? (
        <div className="rgrid">
          {filtered.map((r) => (
            <button className="rcard" key={r.id} onClick={() => onOpen(r.id)}>
              <div className="rcard-top">
                <VenueTag venue={r.venue} />
                <span style={{ color: "var(--muted)", display: "flex" }}>
                  <Arrow size={16} />
                </span>
              </div>
              <h3>{r.name}</h3>
              <p className="meta">
                {r.style} · {r.hood}
              </p>
              <p className="addr">
                <Pin size={14} />
                {fullAddress(r)}
              </p>
              <div className="rcard-foot">
                <span className="items">
                  <List size={14} /> {r.menu.length}{" "}
                  {r.menu.length === 1 ? "item" : "items"}
                </span>
                <span className="open-x">
                  {canEdit(r.id) ? "Edit" : "View"} <Arrow size={14} />
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "var(--muted)",
          }}
        >
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Search size={30} />
          </div>
          <p
            style={{ fontWeight: 700, color: "var(--ink)", margin: "0 0 4px" }}
          >
            No matches
          </p>
          <p style={{ margin: 0, fontSize: 14 }}>
            Try a different search or filter.
          </p>
        </div>
      )}
    </div>
  );
}
